import {
  addToWindowRef,
  ensureDefined,
  HpPromise,
  HpPromiseResolved,
  HpRequestBodyOptions,
  runPromisesSerially,
  SMap,
  urlTemplateResolver
} from '@avantia/client-and-server-utilities';
import { validateFields } from '@avantia/form-validation';
import { hasOwnProperty, HpError } from '@avantia/logging-and-errors';
import {
  createControlId,
  destructureControlId,
  getIdentifierConfigDefaultValues,
  HpActionPropTypes,
  HpControlActionTypes,
  HpCustomScriptActionProps,
  HpSubmitFormActionProps,
  HpValidateFieldsActionProps,
  HpValidateGroupActionProps,
  identifierConfigChanged
} from '@avantia/questionset-model';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logError } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { HpCreateActionPromise } from '../configureFeatures';
import { getCustomExtension } from '../customExtensionRegistry';
import {
  HpCustomExtensionTypes,
  HpCustomFailureHandlerResponse,
  HpDependentActionRunnerCustomFailureHandlerFunction,
  HpDependentActionSubmitFormHandlerFunction,
  HpDependentActionSubmitFormHandlerFunctionProps
} from '../customExtensionRegistryTypes';
import { getValidationForEachSection } from '../reducers/getValidationForEachSection';
import { HpFieldsFailedValidationPayload } from '../reducers/questionSetReducer';
import { createValidationModel, evaluateCondition } from '../reducers/questionSetReducerLibrary';
import { getTypeOfQuestion } from '../reducers/questionSetTools';
import { invokeCustomScript } from '../tools/dynamicScripts';
import { HpClientPrerequisiteThisReferenceClass } from '../tools/prerequisiteThisReference';
import { createVoidEvent } from '../tools/utilities';
import {
  HpAction,
  HpClientConditionFunction,
  HpClientConditionFunctionProps,
  HpClientCustomActionFunction,
  HpClientCustomActionResponseModelTypes,
  HpClientPrerequisiteThisReference,
  HpClientQuestion,
  HpClientQuestionSetState,
  HpCreateActionPromisesProps,
  HpCreateDependentActionRunnerProps,
  HpDependentActionFunction,
  HpDispatchFunction,
  HpEventTypes,
  HpFieldChangedPayload,
  HpScriptActionTypes
} from '../types';
import { HpDynamicButtonClickPayload } from './dynamicButtonClickActionTypes';
import { HpEventPayload } from './eventActionTypes';
import { fetchResolver } from './fetchResolver';

// this holds a map of feature actions, which allows features to be pluggable.
const features: SMap<HpCreateActionPromise<unknown>> = {};

let dependentActionTimeoutMs: number;
let environment: string;
let { groupStartNumber } = getIdentifierConfigDefaultValues();
let scrollPreference: HpScriptActionTypes;
const defaultScrollPreference: HpScriptActionTypes = 'scroll-to-top';

const defaultDependentActionHandler: HpDependentActionSubmitFormHandlerFunction = () => {
  const name: HpCustomExtensionTypes = 'dependentActionSubmitFormHandler';
  throw new HpError(`You need to register a handler using "registerCustomExtension('${name}', ...)".`);
};

registerConfigurationAction((config) => {
  ({ dependentActionTimeoutMs, environment, scrollPreference = defaultScrollPreference } = config);
});

identifierConfigChanged(({ groupStartNumber: gsn }) => {
  groupStartNumber = gsn;
});

export function createDependentActionRunner(
  props: HpCreateDependentActionRunnerProps
): HpDependentActionFunction<unknown> | undefined {
  let promiseList: HpDependentActionFunction<unknown>[] = [];
  const { dependentActions, dispatchAction } = props;
  if (!dependentActions || dependentActions.length === 0) {
    return undefined;
  }

  const { payload, question, questionSet, questionSetId } = props;
  promiseList = dependentActions
    .map((dependentAction) =>
      createActionPromises({
        dependentAction,
        question,
        payload,
        questionSetId,
        questionSet
      })
    )
    .filter((fn) => fn !== undefined) as HpDependentActionFunction<unknown>[];

  if (promiseList.length === 0) {
    return undefined;
  }

  const func: HpDependentActionFunction<unknown> = (dispatch: HpDispatchFunction<unknown>): Promise<boolean | void> => {
    const funList: HpPromise<boolean | void>[] = promiseList.map((fn, index) => {
      return {
        name: `dependent-action-${index + 1}`,
        action: (): Promise<boolean | void> => {
          return fn(dispatch);
        }
      };
    });

    return new Promise<boolean>((resolve, reject) => {
      return runPromisesSerially(funList, (res) => res !== false)
        .then((results: HpPromiseResolved<boolean | void>[]) => {
          if (dispatchAction) {
            // Only dispatch when no promise has returned false
            if (!results.find((r) => r.result === false)) {
              // This is only required when the 'field changed' event needs to be triggered on successful completion of dependent actions.
              dispatchAction(dispatch);
            }
          }

          resolve(true);
        })
        .catch(reject);
    });
  };

  return func;
}

/**
 * This allows an action to be dispatched and also prevents any subsequent actions from taking place (when success=false).
 */
export interface HpActionSuccessType {
  success?: boolean;
}

function createActionPromises(properties: HpCreateActionPromisesProps): HpDependentActionFunction<unknown> | undefined {
  const { dependentAction, payload: eventPayload, question, questionSet } = properties;
  const { action, condition, props: actionProps } = dependentAction;
  const { questionId } = question || {};

  if (action === 'validate-fields') {
    return createValidateFieldsAction({
      actionProps: actionProps as HpValidateGroupActionProps,
      condition,
      eventPayload: eventPayload as HpFieldChangedPayload,
      questionId,
      questionSet
    });
  } else if (features[action]) {
    if (condition) {
      if (
        !evaluateCondition({
          questionSet,
          condition,
          debugFlag: HpClientDebugFlags.DependentActions,
          eventType: 'none',
          questionId,
          sourceQuestionId: questionId
        })
      ) {
        return undefined;
      }
    }

    // perform an action upon an optional feature that has been registered via the registerActionPromise(...) function
    return features[action](properties);
  } else if (action === 'submit-form') {
    if (condition) {
      if (!evaluateSubmitFormCondition(condition, questionId, questionSet)) {
        return undefined;
      }
    }

    const {
      submitUri: submitUriTemplate,
      stateProperties,
      validateFields,
      clearExistingErrors,
      continueOnSuccess
    } = (actionProps as HpSubmitFormActionProps) || {};

    const data: SMap<any> = {};
    stateProperties.forEach((key) => {
      data[key] = questionSet[key] || null;
    });

    const submitUri = urlTemplateResolver({ template: submitUriTemplate, model: data });
    const submitFormAction: HpDependentActionFunction<unknown> = (dispatch) => {
      addToWindowRef({ submitFormResult: null });
      return new Promise<boolean>((resolve, reject) => {
        if (validateFields !== false) {
          const { areValid, validation } = areFieldsValid({
            actionProps,
            eventPayload,
            questionSet,
            scrollTo: scrollPreference
          });

          if (!areValid) {
            dispatch({ type: 'FIELDS_FAILED_VALIDATION', payload: validation });
            return resolve(areValid);
          }
        }

        if (clearExistingErrors === true) {
          dispatch({ type: 'FIELDS_PASSED_VALIDATION' });
        }

        const options: HpRequestBodyOptions<SMap<any>> = {
          url: submitUri,
          data,
          method: 'POST',
          description: `Dependent action (${action})`,
          timeout: dependentActionTimeoutMs
        };

        fetchResolver<SMap<any>, unknown>({
          options,
          loadingPageType: 'page',
          dispatch,
          successAction: (response) => {
            const dependentActionHandler = (getCustomExtension('dependentActionSubmitFormHandler') ||
              defaultDependentActionHandler) as HpDependentActionSubmitFormHandlerFunction;

            const handlerProps: HpDependentActionSubmitFormHandlerFunctionProps = {
              dependentAction,
              dispatch,
              requestOptions: options,
              response
            };
            dependentActionHandler(handlerProps);
          },
          failedAction: (reason) => {
            const dependentActionFailedHandler =
              getCustomExtension<HpDependentActionRunnerCustomFailureHandlerFunction>(
                'dependentActionSubmitFormFailedHandler'
              );

            const resp: HpCustomFailureHandlerResponse = dependentActionFailedHandler
              ? dependentActionFailedHandler({
                  reason,
                  options,
                  questionId,
                  dependentAction,
                  dispatch
                })
              : undefined;

            if (resp) {
              return resp;
            } else {
              const detail = reason === 'string' ? reason : JSON.stringify(reason, null, 2);
              const { responseData } = (reason as any) || {};
              const { errorCode } = responseData || ({} as { errorCode?: string });

              if (errorCode === 'EPLK') {
                alert('Your changes could not be saved because the Quote is currently locked by an Agent');
              } else if (
                detail.indexOf('The save to TCAS was successful but we do not want to progress further') >= 0
              ) {
                alert(
                  'The Quote details have been successfully saved to TCAS\n\n' +
                    'Unfortunately we cannot yet progress further:\n' +
                    'To do so would save details to NHI which will in turn overwrite TCAS records with its own changes'
                );
              } else {
                const messageToLog = `Failed to ${action}\n\n${detail}`;
                logError(messageToLog);
                if (['developer', 'dev', 'tst'].indexOf(environment) >= 0) {
                  if (
                    messageToLog.indexOf('The save to TCAS was successful but we do not want to progress further') >= 0
                  ) {
                    alert(
                      'The Quote details have been successfully saved to TCAS\n\n' +
                        'Unfortunately we cannot yet progress further:\n' +
                        'To do so would save details to NHI which will in turn overwrite TCAS records with its own changes'
                    );
                  } else {
                    alert(messageToLog);
                  }
                }
              }
            }
          }
        })
          .then(() => {
            // don't perform any further action, unless 'continueOnSuccess' is true.
            resolve(continueOnSuccess === true || false);
          })
          .catch(reject);
      });
    };

    return submitFormAction;
  } else if (action === 'custom') {
    if (condition) {
      if (
        !evaluateCondition({
          questionSet,
          condition,
          debugFlag: HpClientDebugFlags.DependentActions,
          eventType: 'none',
          questionId,
          sourceQuestionId: questionId
        })
      ) {
        return undefined;
      }
    }

    const event = eventPayload ? eventPayload.getEvent() : createVoidEvent();
    const props = dependentAction.props as HpCustomScriptActionProps;
    const customScript = props.customScript as unknown as HpClientCustomActionFunction;
    const validateFieldsAction: HpDependentActionFunction<unknown> = (dispatch) => {
      return new Promise<boolean>((resolve, reject) => {
        const response = invokeCustomScript({
          customScript,
          dispatch,
          questionId: questionId || '',
          eventType: event.type as HpEventTypes,
          payload: eventPayload as HpFieldChangedPayload,
          questionSet
        });

        const promise =
          response && !!(response as any).then
            ? (response as Promise<HpClientCustomActionResponseModelTypes>)
            : Promise.resolve(response as HpClientCustomActionResponseModelTypes);

        promise
          .then((actionResponse) => {
            const action = actionResponse as HpAction<any> & HpActionSuccessType;
            let isSuccess = actionResponse !== false;

            if (action) {
              if (!action.type) {
                throw new Error(
                  "If a custom script returns an action, it must contain at the very least a 'type' property (and usually a payload)."
                );
              }

              dispatch(action);
              if (hasOwnProperty(action, 'success')) {
                isSuccess = action.success !== false;
              }
            }

            resolve(isSuccess);
          })
          .catch(reject);
      });
    };

    return validateFieldsAction;
  } else {
    switch (action) {
      case 'add-control-group':
      case 'remove-control-group':
      case 'next-section':
      case 'previous-section':
        // These actions are handled elsewhere, usually by the updateAnswer function.
        break;
      default:
        // Some actions do not need to be executed when a user performs an action. These are generally executed in the
        // updateAnswer function and usually affect other answers than that interacted by the user.
        logError(`The "${action}" action is currently ignored.`);
        break;
    }
  }

  return undefined;
}

interface HpAreFieldsValidResult {
  areValid: boolean;
  validation: HpFieldsFailedValidationPayload | undefined;
}

interface HpAreFieldsValidProps {
  actionProps: HpActionPropTypes | undefined;
  eventPayload: HpEventPayload | undefined;
  questionSet: HpClientQuestionSetState;
  scrollTo: HpScriptActionTypes;
}

function areFieldsValid({
  actionProps,
  eventPayload,
  questionSet,
  scrollTo
}: HpAreFieldsValidProps): HpAreFieldsValidResult {
  let { fieldsToValidate } = (actionProps as HpValidateFieldsActionProps) || {};
  const { groupId } = (actionProps as HpValidateGroupActionProps) || {};
  let validation: HpFieldsFailedValidationPayload | undefined;
  if (groupId) {
    const section = ensureDefined(questionSet.displayState.sections[groupId], `sections['${groupId}']`);
    fieldsToValidate = section.questionIds || [];
    const { key } = (eventPayload as HpFieldChangedPayload | undefined) || {};
    if (key && fieldsToValidate) {
      const { groupNumber, indexes } = destructureControlId(key, { requirePrefix: false });
      if (groupStartNumber !== undefined) {
        fieldsToValidate = fieldsToValidate.map((questionId) =>
          createControlId({ questionId, groupNumber, indexes, options: { noPrefix: true } })
        );
      }
    }
  }

  if (fieldsToValidate && fieldsToValidate.length) {
    const event = (eventPayload ? eventPayload.getEvent() : undefined) || createVoidEvent();
    const { groupNumber } = (eventPayload as HpDynamicButtonClickPayload | undefined) || {};
    if (groupNumber) {
      fieldsToValidate = fieldsToValidate.map((f) =>
        createControlId({ questionId: f, groupNumber, options: { noPrefix: true } })
      );
    }

    const validationResult = validateFields({
      fields: createValidationModel(questionSet),
      fieldKeysToValidate: fieldsToValidate || undefined,
      previousResult: undefined,
      eventType: event.type
    });

    if (validationResult.count > 0) {
      validation = {
        scrollTo,
        sectionIdWithErrors: questionSet.displayState.activeSectionId,
        validation: { ...questionSet.validationResult, ...validationResult }
      };
    }
  } else {
    const { sectionValidationMap, firstSectionIdWithErrors, validSections } = getValidationForEachSection({
      state: questionSet,
      raiseNavigationChangedEvent: false
    });

    if (firstSectionIdWithErrors) {
      validation = {
        scrollTo,
        sectionIdWithErrors: firstSectionIdWithErrors,
        validation: {
          ...questionSet.validationResult,
          ...sectionValidationMap[firstSectionIdWithErrors],
          validSections
        }
      };
    }
  }

  return { areValid: !validation, validation };
}

function evaluateSubmitFormCondition(
  condition: HpClientConditionFunction,
  questionId: string,
  questionSet: HpClientQuestionSetState
): boolean {
  const { answers, commonLookups, displayState, productId, questions } = questionSet;
  const { sections } = displayState;
  const questionType = getTypeOfQuestion(questionId, questions, sections);
  const self: HpClientPrerequisiteThisReference<HpClientQuestion> = new HpClientPrerequisiteThisReferenceClass({
    answers,
    commonLookups,
    debugFlag: HpClientDebugFlags.PayloadMapper,
    displayState,
    groupNumber: undefined,
    indexes: undefined,
    productId,
    questions,
    sections: displayState.sections,
    sourceQuestionId: questionId,
    sourceQuestionType: questionType
  });

  const conditionProps: HpClientConditionFunctionProps = {
    eventType: 'none',
    groupNumber: undefined,
    indexes: undefined,
    lookupKey: '',
    questionId,
    questionType,
    sourceQuestionId: questionId
  };

  // This 'condition' evaluation has a reduced feature-set because it executes outside of the reducer.
  // For example, you may need to use 'this.getAnswerValueByName(name)' instead of 'this.getAnswerValue(...)'
  return condition.apply(self, [conditionProps]);
}

export function registerActionPromise(
  key: HpControlActionTypes,
  createActionPromise: HpCreateActionPromise<unknown>
): void {
  features[key] = createActionPromise;
}

interface HpCreateValidateFieldsActionProps {
  actionProps: HpValidateGroupActionProps | undefined;
  condition: HpClientConditionFunction | undefined;
  eventPayload: HpFieldChangedPayload | undefined;
  questionId: string;
  questionSet: HpClientQuestionSetState;
}

function createValidateFieldsAction({
  actionProps,
  condition,
  eventPayload,
  questionId,
  questionSet
}: HpCreateValidateFieldsActionProps): HpDependentActionFunction<unknown> | undefined {
  if (condition) {
    const { getEvent, key } = ensureDefined(eventPayload, 'eventPayload');
    const { type: eventType } = getEvent();
    const { changeFieldOnlyOnSuccess } = actionProps || {};
    const { groupNumber, indexes } = destructureControlId(key, { requirePrefix: false });
    const conditionMet = evaluateCondition({
      questionSet,
      condition,
      groupNumber,
      indexes,
      debugFlag: HpClientDebugFlags.DependentActions,
      eventType: eventType as HpEventTypes,
      questionId,
      sourceQuestionId: questionId
    });

    if (!conditionMet) {
      if (!changeFieldOnlyOnSuccess) {
        return undefined;
      } else {
        const nothingFunc: HpDependentActionFunction<unknown> = () => {
          return Promise.resolve(true);
        };

        return nothingFunc;
      }
    }
  }

  const validateFieldsAction: HpDependentActionFunction<unknown> = (dispatch) => {
    return new Promise<boolean>((resolve) => {
      const { areValid, validation } = areFieldsValid({
        actionProps,
        eventPayload,
        questionSet,
        scrollTo: scrollPreference
      });
      dispatch({ type: !validation ? 'FIELDS_PASSED_VALIDATION' : 'FIELDS_FAILED_VALIDATION', payload: validation });
      return resolve(areValid);
    });
  };

  return validateFieldsAction;
}
